import { Injectable } from '@angular/core'
import { TruckAndDevice } from '../classes/truck-and-device'
import { LocationType } from '../enums/location'
import { LatLng } from '../interfaces/lat-lng'
import { Location, MapLocation } from '../interfaces/location'
import { GpsLog } from '../interfaces/truck'
import { ColorService } from './color.service'
import { TruckInfoCoreSensors } from 'projects/appsync/src/lib/appsync.service'
import {
  Geolocation,
  GeolocationItem,
  TruckSnapshot,
  TruckTimeseries,
} from 'projects/appsync/src/lib/API.service'
import { NUMBER_EMPTY } from '../constants/location'

@Injectable({
  providedIn: 'root',
})
export class LocationHelpersService {
  public markerUrlPlant = '/assets/icons/pin-plant-48x48.png'
  public markerUrlSite = '/assets/icons/pin-site-48x48.png'
  public markerUrlPour = '/assets/icons/pin-pour-48x48.png'
  public markerUrlTruck = '/assets/icons/pin-truck-grey.svg'
  public markerUrlTruckSelected = '/assets/icons/pin-truck.svg'

  constructor(private color: ColorService) {}

  public parseLocationString(loc: string) {
    let ret: LatLng | undefined = undefined
    if (loc?.length) {
      const seperator = loc.indexOf(',') > -1 ? ',' : '/' // some locations use / instead of ,
      const arr = loc.split(seperator)
      if (arr.length === 2) {
        ret = { lat: parseFloat(arr[0]), lng: parseFloat(arr[1]) } as LatLng
      }
    }
    return ret
  }

  public filterTrucksByBatchPlant(
    trucks: TruckAndDevice[],
    geofenceId: string,
    locationOwnerId: string,
  ) {
    if (!location) {
      return trucks
    }
    return trucks.filter((truck) => {
      // default to show
      let docketMatch = true
      const currentGeofenceMatch = !!(
        truck.plantGeofenceIdMostRecent &&
        truck.plantGeofenceIdMostRecent === geofenceId
      )

      const docketPlantId = this.locationOwnerIdFromClientId(
        truck.plantCode,
        'BASE',
      )
      docketMatch = locationOwnerId === docketPlantId
      return currentGeofenceMatch || docketMatch
    })
  }

  public filterTrucksByDeliverySite(
    trucks: TruckAndDevice[],
    geofenceId: string,
    locationOwnerId: string,
  ) {
    if (!location) {
      return trucks
    }
    return trucks.filter((truck) => {
      // default to show
      let docketMatch = true
      const currentGeofenceMatch = !!(
        truck.plantGeofenceIdMostRecent &&
        truck.plantGeofenceIdMostRecent === geofenceId
      )

      const docketSiteId = this.locationOwnerIdFromClientId(
        truck.jobsiteCode,
        'SITE',
      )
      docketMatch = locationOwnerId === docketSiteId
      return currentGeofenceMatch || docketMatch
    })
  }

  public filterTrucksByGeofence(
    trucks: TruckAndDevice[],
    location: MapLocation,
  ) {
    if (!location) {
      return trucks
    }
    const locationGeofenceId = this.geofenceId(location.owner, location.ownerId)
    return trucks.filter((truck) => {
      // default to show
      let docketMatch = true
      const currentGeofenceMatch = !!(
        truck.plantGeofenceIdMostRecent &&
        truck.plantGeofenceIdMostRecent === locationGeofenceId
      )

      if (location.type === 'SITE') {
        const docketSiteId = this.locationOwnerIdFromClientId(
          truck.jobsiteCode,
          location.type,
        )
        docketMatch = location.ownerId === docketSiteId
      } else if (location.type === 'BASE') {
        const docketPlantId = this.locationOwnerIdFromClientId(
          truck.plantCode,
          location.type,
        )
        docketMatch = location.ownerId === docketPlantId
      }
      return currentGeofenceMatch || docketMatch
    })
  }

  public fixInvalidLocations<T extends GpsLog>(logs: T[]): T[] {
    let lastValidLat: number | null = null
    let lastValidLng: number | null = null
    return (logs || []).filter((log) => {
      const isValid = this.latLngIsValid({
        lat: log.gpsLatitudeDecimal,
        lng: log.gpsLongitudeDecimal,
      })
      if (isValid) {
        // store valid values
        lastValidLat = log.gpsLatitudeDecimal
        lastValidLng = log.gpsLongitudeDecimal
        return true
      } else if (
        typeof lastValidLat === 'number' &&
        typeof lastValidLng === 'number'
      ) {
        // not valid but we have a previous valid value
        log.gpsLatitudeDecimal = lastValidLat
        log.gpsLongitudeDecimal = lastValidLng
        return true
      } else {
        // not valid and not seen a valid location yet
        return false
      }
    })
  }

  public hasValidGPS(item: TruckTimeseries | TruckSnapshot): boolean {
    return this.latLngIsValid({
      lat: item.gpsLatitudeDecimal,
      lng: item.gpsLongitudeDecimal,
    })
  }

  public latLngIsValid(point: LatLng) {
    if (!point) {
      return false
    }
    // null check
    if (typeof point.lat !== 'number' || typeof point.lng !== 'number') {
      return false
    }
    // Nan check
    if (isNaN(point.lat) || isNaN(point.lng)) {
      return false
    }
    // NullIsland check
    if (point.lat === 0 && point.lng === 0) {
      return false
    }
    // EmptyNumber check
    if (point.lat === NUMBER_EMPTY || point.lng === NUMBER_EMPTY) {
      return false
    }
    return true
  }

  public removeInvalidPoints(points: LatLng[]) {
    return points.filter((point) => this.latLngIsValid(point))
  }

  public pointsToSixDP(points: LatLng[]) {
    const cleanedPoints = points.map(
      (point) =>
        ({
          lat: this.toSixDecimalPlaces(point.lat),
          lng: this.toSixDecimalPlaces(point.lng),
        }) as LatLng,
    )
    return cleanedPoints
  }

  public parseOneHertzLocations(
    latitudes: string | null,
    longitudes: string | null,
  ) {
    let latitudesParsed: number[] | null = null
    let longitudesParsed: number[] | null = null

    try {
      latitudesParsed = JSON.parse(latitudes)
    } catch (error) {
      // failed to parse latitudes
    }
    try {
      longitudesParsed = JSON.parse(longitudes)
    } catch (error) {
      // failed to parse longitudes
    }

    let locationUpdates: LatLng[] = []
    // check if latitudes and longitudes are arrays or more than zero items and are the same length
    const validLatLongs =
      Array.isArray(latitudesParsed) &&
      Array.isArray(longitudesParsed) &&
      latitudesParsed.length &&
      longitudesParsed.length &&
      latitudesParsed.length === longitudesParsed.length
    if (validLatLongs) {
      // use latitudes and longitudes arrays to calculate the location
      locationUpdates = latitudesParsed.map((lat, i) => ({
        lat: lat,
        lng: longitudesParsed[i],
      }))
    }
    return locationUpdates
  }

  public routePointsToPaths(
    points: LatLng[],
    maxNoiseRunLength = 5,
  ): LatLng[][] {
    if (!points || points.length < 2) {
      return []
    }
    // split the points into a new path whenever there is a gap - but treat
    // a short, bounded run of invalid points (valid points immediately
    // either side of the run, and no more than maxNoiseRunLength of them)
    // as GPS noise rather than a real gap: a handful of bad samples
    // surrounded by good ones on both sides is far more likely a momentary
    // glitch than an actual reception gap, so the run is dropped and the
    // path stays connected across it. A run longer than maxNoiseRunLength,
    // or one that isn't bounded by valid points on both sides (runs off
    // the start/end of the data), is still treated as a genuine gap and
    // splits the path.
    const paths: LatLng[][] = []
    let currentPath: LatLng[] = []
    let i = 0
    while (i < points.length) {
      if (this.latLngIsValid(points[i])) {
        currentPath.push(points[i])
        i++
        continue
      }
      // find the full extent of this run of invalid points
      let runEnd = i
      while (runEnd < points.length && !this.latLngIsValid(points[runEnd])) {
        runEnd++
      }
      const runLength = runEnd - i
      const boundedByValidPoints = i > 0 && runEnd < points.length
      const isBridgeableNoise =
        boundedByValidPoints && runLength <= maxNoiseRunLength
      if (!isBridgeableNoise && currentPath.length > 0) {
        paths.push(currentPath)
        currentPath = []
      }
      i = runEnd
    }
    // push any remaining path
    if (currentPath.length > 0) {
      paths.push(currentPath)
    }
    return paths
  }

  public routePoints(
    logs: TruckInfoCoreSensors[],
    useOneHzLocations: boolean,
    removeInvalid = true,
  ): LatLng[] {
    let points: LatLng[] = []
    logs.forEach((log) => {
      // check for 1Hz data
      if (log.locationUpdates?.length && useOneHzLocations) {
        // there is 1Hz data so use the 10 LatLngs it provides
        const locationUpdates = log.locationUpdates
        points.push(...locationUpdates)
      } else {
        // no 1Hz data so use the single LatLng
        const latLng = {
          lat: log.gpsLatitudeDecimal,
          lng: log.gpsLongitudeDecimal,
        } as LatLng
        points.push(latLng)
      }
    })
    if (removeInvalid) {
      points = this.removeInvalidPoints(points)
    }
    return this.pointsToSixDP(points)
  }

  public batcherOwnerIdFromBase(ownerId: string, childCount: number) {
    // BASE can have multiple BATCHERs, so include a number to keep the ids unique
    return `${ownerId}__${childCount + 1}__BATCHER`
  }

  public locationOwnerIdWithSuffix(location: Location) {
    const batcherSuffix = '__BATCHER'
    const siteSuffix = '__SITE'
    const parkingSuffix = '__PARKING'
    const cleanedOwnerId = location.ownerId.split(' ').join('-')
    const upperCaseOwnerId = cleanedOwnerId.toUpperCase()
    let appendSuffix = ''
    switch (location.type) {
      case 'BATCHER':
        if (!upperCaseOwnerId.endsWith(batcherSuffix)) {
          appendSuffix = batcherSuffix
        }
        break
      case 'SITE':
        if (!upperCaseOwnerId.endsWith(siteSuffix)) {
          appendSuffix = siteSuffix
        }
        break
      case 'PARKING':
        if (!upperCaseOwnerId.endsWith(parkingSuffix)) {
          appendSuffix = parkingSuffix
        }
    }
    return cleanedOwnerId + appendSuffix
  }

  public locationValidationErrors(location: Location): string[] {
    const errs = []
    if (!location.owner) {
      errs.push('Owner is required')
    }
    if (!location.ownerId) {
      errs.push('OwnerId is required')
    }
    if (!location.name) {
      errs.push('Name is required')
    }
    if (location.type === 'BATCHER' && !location.parentId) {
      errs.push('A batcher must have a parentId')
    }
    return errs
  }

  public locationIsBase(id: string) {
    // BASEs were orig the only locn types so don't have type appended
    // All other types will have __<TYPE> appended to the id
    return id.split('__').length === 1
  }

  public locationTypeFromId(id: string): LocationType {
    const ar = id.split('__')
    if (ar.length === 1) {
      return 'BASE'
    } else {
      return ar[1] as LocationType
    }
  }

  public locationOwnerIdFromClientId(id: string, locationType: LocationType) {
    if (!id) {
      return ''
    }
    if (locationType === 'BASE') {
      // BASEs were orig the only locn types so don't have type appended
      return id
    }
    // All other types will have __<TYPE> appended to the id
    // this is to avoid clashing id's
    return `${id}__${locationType}`
  }

  public ownerIdFromGeofenceId(geofenceId: string) {
    if (geofenceId && geofenceId.includes('--')) {
      return geofenceId.split('--')[1]
    }
    return ''
  }

  public geofenceId(owner: string, ownerId: string) {
    if (owner && ownerId) {
      return `${owner}--${ownerId}`
    }
    return ''
  }

  public locationsAreTheSame(a: Location, b: Location) {
    // consider them to be the same object if they have the same owner, id, status and polygon
    return (
      a.owner === b.owner &&
      a.ownerId === b.ownerId &&
      a.polygon === b.polygon &&
      a.status === b.status
    )
  }

  public awsGeofenceStringToGoogleMapsPolygon(geofenceStr: string) {
    const geofenceArr = this.awsGeofenceStringToGeofenceArray(geofenceStr)
    const mapPolygon = this.awsGeofenceArrayToGoogleMapsPolygon(geofenceArr)
    return mapPolygon
  }

  public awsGeofenceStringToGeofenceArray(geofenceStr: string): number[][][] {
    return geofenceStr ? (JSON.parse(geofenceStr) as number[][][]) : []
  }

  public locationUpdateToMapLocation(update: GeolocationItem) {
    const geofenceArr = this.awsGeofenceStringToGeofenceArray(update.polygon)
    const geofenceIsMaxSixDecimals =
      this.geofenceIsMaxSixDecimalPlaces(geofenceArr)
    const geofenceIsClockwise = this.awsPathIsClockwise(geofenceArr[0])
    const mapPolygon = this.awsGeofenceArrayToGoogleMapsPolygon(geofenceArr)

    return {
      owner: update.owner,
      ownerId: update.owner_id,
      parentId: update.parent_id || '',
      name: update.name || '',
      lng: update.lng || '',
      lat: update.lat || '',
      loadingPoints: (update.loading_points || '').split(','),
      type: update.type || 'BASE',
      polygon: update.polygon || '',
      status: update.status || '',
      timezone: update.timezone || '',
      timezoneDate: update.timezone_date || '',
      geofence: mapPolygon,
      geofenceIsMaxSixDecimals,
      geofenceIsClockwise,
    } as MapLocation
  }

  public locationToMapLocation(result: Geolocation) {
    if (!result) {
      return null
    }
    const geofenceArr = this.awsGeofenceStringToGeofenceArray(result?.Polygon)
    const geofenceIsMaxSixDecimals =
      this.geofenceIsMaxSixDecimalPlaces(geofenceArr)
    const geofenceIsClockwise = this.awsPathIsClockwise(geofenceArr[0])
    const mapPolygon = this.awsGeofenceArrayToGoogleMapsPolygon(geofenceArr)
    const locationType = (result.Type || 'BASE') as LocationType
    const loadingPoints = (result.LoadingPoints || '').trim()
    // js split on empty string returns array with one empty item
    const loadingPointsArr = loadingPoints.length
      ? loadingPoints.split(',')
      : []

    return {
      owner: result.Owner,
      ownerId: result.OwnerId,
      parentId: result.ParentId || '',
      name: result.Name || '',
      lng: result.Lng || '',
      lat: result.Lat || '',
      type: locationType,
      polygon: result.Polygon || '',
      status: result.Status || '',
      timezone: result.Timezone || '',
      timezoneDate: result.TimezoneDate || '',
      loadingPoints: loadingPointsArr,
      lastDeliveryDate: result.LastDeliveryTimestamp
        ? new Date(result.LastDeliveryTimestamp).toISOString()
        : '',
      geofence: mapPolygon,
      geofenceIsMaxSixDecimals,
      geofenceIsClockwise,
      polygonOptions: this.polygonOptions(
        locationType,
        geofenceIsClockwise,
        false,
      ),
    } as MapLocation
  }

  public awsGeofenceArrayToGoogleMapsPolygon(geofence: number[][][]) {
    const isValid =
      geofence.length === 1 && geofence[0].length && geofence[0][0].length === 2

    if (isValid) {
      // https://docs.aws.amazon.com/location-geofences/latest/APIReference/API_GeofenceGeometry.html
      // first is the outer boundary
      const boundary = geofence[0]
      if (this.isAwsPathClosed(boundary)) {
        // aws geofence has to have identical latLng at start and end.
        // google does not so remove
        boundary.pop()
      }
      return boundary.map((item) => ({
        lat: item[1],
        lng: item[0],
      }))
    }
    return null
  }

  public latLngsToAwsPath(points: LatLng[]) {
    const path = points.map((point) => [point.lng, point.lat])
    return this.closeAwsPath(path)
  }

  public isAwsPathClosed(path: number[][]) {
    if (path && path.length > 2) {
      const first = path[0]
      const last = path[path.length - 1]
      return first[0] === last[0] && first[1] === last[1]
    }
    return false
  }

  public closeAwsPath(path: number[][]) {
    if (path && path.length > 2) {
      if (!this.isAwsPathClosed(path)) {
        path.push(path[0])
      }
    }
    return path
  }

  public latLngsToAwsGeofence(points: LatLng[]) {
    const ret: number[][][] = []
    // check validity
    if (points && points.length) {
      // convert to array of [lng,lat]
      const outerPath = this.latLngsToAwsPath(points)
      // const outerPath = polygon[0];
      if (this.pathIsValid(outerPath)) {
        // ensure the path is counter-clockwise
        const counterClockwisePath = this.counterClockwisePath(outerPath)
        // ensure each point is a max of 6dp
        const toSixPath = this.pathToSixDecimalPlaces(counterClockwisePath)
        ret.push(this.pathToSixDecimalPlaces(toSixPath))
      }
    }
    return ret
  }

  public geofenceIsMaxSixDecimalPlaces(geofence: number[][][]) {
    let valid = true
    if (geofence && geofence.length) {
      const polygon = geofence[0]
      polygon.forEach((coord) => {
        if (this.countDecimals(coord[0]) > 6) {
          valid = false
        }
        if (this.countDecimals(coord[1]) > 6) {
          valid = false
        }
      })
    } else {
      valid = false
    }
    return valid
  }

  public toSixDecimalPlaces(num: number) {
    // AWS Location Service accepts location data up to
    // six decimal places of precision (0.000001)
    // which equals to approximately 11 cm or 4.4 inches at the equator
    return Math.round(num * 1000000) / 1000000
  }

  public pathToSixDecimalPlaces(path: number[][]) {
    return path.map((point) => [
      this.toSixDecimalPlaces(point[0]),
      this.toSixDecimalPlaces(point[1]),
    ])
  }

  public geofenceToSixDecimalPlaces(geofence: number[][][]) {
    // AWS Location Service accepts location data up to
    // six decimal places of precision (0.000001)
    // which equals to approximately 11 cm or 4.4 inches at the equator
    if (geofence && geofence.length) {
      const path = geofence[0]
      const rounded = this.pathToSixDecimalPlaces(path)
      // re-wrap in geofence array
      return [rounded]
    }
    return geofence
  }

  // a polygon must be 3 or more points
  public pathIsValid(path: number[][]) {
    if (path || path.length > 2) {
      return true
    }
    return false
  }

  // AWS geofence is an array of arrays or arrays
  // Each item in the Outer array is array of: [lng, lat]
  // The first and last points should be the same to complete the loop
  // First item in the outer array is the main geofence
  // it MUST be counter clockwise
  // subsquent items should be:
  // - counter clockwise for satalite/child shapes
  // - counter-clockwise for holes in the main geofence
  // Expects a polygon which has more than 2 points
  // and first and last points are the same
  public awsPathIsClockwise(path: number[][]) {
    const sum = (path || []).reduce((accum, point, index) => {
      const isLast = index === path.length - 1
      if (!isLast) {
        const nextIndex = isLast ? 0 : index + 1
        const x1 = point[0]
        const x2 = path[nextIndex][0]
        const y1 = point[1]
        const y2 = path[nextIndex][1]
        const edge = (x2 - x1) * (y2 + y1)
        accum += edge
      }
      return accum
    }, 0)
    return sum >= 0
  }

  public getLatLngCenter(path: number[][]) {
    if (!path?.length) {
      return null
    }

    const latIDX = 1
    const lngIDX = 0

    // NEW
    let lat = 0.0
    let lon = 0.0
    path.forEach((item) => {
      lat += item[latIDX]
      lon += item[lngIDX]
    })

    const centralLatitude = lat / path.length
    const centralLongitude = lon / path.length

    //OLD

    // let sumX = 0;
    // let sumY = 0;
    // let sumZ = 0;

    // path.forEach(item => {
    //   const lat = this.degr2rad(item[latIDX]);
    //   const lng = this.degr2rad(item[lngIDX]);
    //   // sum of cartesian coordinates
    //   sumX += Math.cos(lat) * Math.cos(lng);
    //   sumY += Math.cos(lat) * Math.sin(lng);
    //   sumZ += Math.sin(lat);
    // });

    // const avgX = sumX / path.length;
    // const avgY = sumY / path.length;
    // const avgZ = sumZ / path.length;

    // // convert average x, y, z coordinate to latitude and longtitude
    // const centralLongitude = this.rad2degr(Math.atan2(avgY, avgX));
    // const hyp = Math.sqrt(avgX * avgX + avgY * avgY);
    // const centralLatitude = this.rad2degr(Math.atan2(avgZ, hyp));

    return {
      lat: this.toSixDecimalPlaces(centralLatitude),
      lng: this.toSixDecimalPlaces(centralLongitude),
    } as LatLng
  }

  // public centralLatLngFromPath(path: number[][]) {

  //   if(path && path.length) {

  //     let x = 0;
  //     let y = 0;
  //     let z = 0;

  //       path.forEach (geoCoordinate => {

  //         const latitude = geoCoordinate[1] * Math.PI / 180;
  //         const longitude = geoCoordinate[0] * Math.PI / 180;

  //         x += Math.cos(latitude) * Math.cos(longitude);
  //         y += Math.cos(latitude) * Math.sin(longitude);
  //         z += Math.sin(latitude);

  //       });

  //       const total = path.length;

  //       x = x / total;
  //       y = y / total;
  //       z = z / total;

  //       const centralLongitude = Math.atan2(y, x);
  //       const centralSquareRoot = Math.sqrt(x * x + y * y);
  //       const centralLatitude = Math.atan2(z, centralSquareRoot);

  //       return  { lat: this.toSixDecimalPlaces(centralLatitude), lng: this.toSixDecimalPlaces(centralLongitude) } as LatLng;
  //   }
  //   return null;
  // }

  public counterClockwisePath(path: number[][]) {
    if (path && path.length) {
      const isClockwise = this.awsPathIsClockwise(path)
      if (isClockwise) {
        // clockwise. create a copy and reverse
        return [...path].reverse()
      }
    }
    return path
  }

  // Sum over the edges, (x2 − x1)(y2 + y1).
  // If the result is positive the curve is clockwise,
  // if it's negative the curve is counter-clockwise.
  public counterClockwiseGeofence(geofence: number[][][]) {
    if (geofence && geofence.length) {
      // outer ring of a geofence is the first path
      const path = geofence[0]
      if (path.length) {
        const isClockwise = this.awsPathIsClockwise(path)
        if (isClockwise) {
          // clockwise. create a copy and reverse
          geofence[0] = [...path].reverse()
        }
      }
      return geofence
    }
    return geofence
  }

  public geofenceColour(edit: boolean, locType: LocationType) {
    if (edit) {
      return this.color.locationEdit
    } else if (locType === 'SITE') {
      return this.color.locationSite
    } else if (locType === 'BASE') {
      return this.color.locationBase
    } else if (locType === 'BATCHER') {
      return this.color.locationBatcher
    } else if (locType === 'PARKING') {
      return this.color.locationParking
    } else if (locType === 'RECYCLING') {
      return this.color.locationRecycling
    }
  }

  public polygonOptions(
    locType: LocationType,
    geofenceIsClockwise: boolean,
    edit: boolean,
  ) {
    if (!locType) {
      return null
    }
    const strokeColor = geofenceIsClockwise
      ? '#FF0000'
      : this.geofenceColour(edit, locType)
    return {
      strokeColor,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: this.geofenceColour(edit, locType),
      fillOpacity: 0.35,
      editable: edit,
      draggable: edit,
    }
  }

  private countDecimals(num: number) {
    if (Math.floor(num) === num) {
      return 0
    }
    return num.toString().split('.')[1].length || 0
  }

  private rad2degr(rad) {
    return (rad * 180) / Math.PI
  }
  private degr2rad(degr) {
    return (degr * Math.PI) / 180
  }
}
